/** Pauta fija del examen práctico de conducción clase B. */

export type PracticalExamSeverity = 'L' | 'G' | 'R';
export type PracticalExamVehicle = 'mechanical' | 'automatic';
export type PracticalExamSheetStatus = 'draft' | 'final';

export const PRACTICAL_EXAM_WIZARD_STEPS = [
  'student',
  'vehicle',
  'km',
  'sheet',
  'km-end',
  'student-sign',
  'examiner-sign',
] as const;

export type PracticalExamWizardStep = (typeof PRACTICAL_EXAM_WIZARD_STEPS)[number];

export function isPracticalExamWizardStep(value: unknown): value is PracticalExamWizardStep {
  return typeof value === 'string' && (PRACTICAL_EXAM_WIZARD_STEPS as readonly string[]).includes(value);
}

export interface PracticalExamFault {
  id: string;
  code: string;
  label: string;
  severity: PracticalExamSeverity;
}

export interface PracticalExamBlock {
  id: string;
  title: string;
  column: 'left' | 'right';
  faults: PracticalExamFault[];
}

export interface PracticalExamTotals {
  mild: number;
  serious: number;
  failing: number;
}

export interface PracticalExamScore extends PracticalExamTotals {
  passed: boolean;
  subtotals: Record<string, PracticalExamTotals>;
}

export interface PracticalExamSheetInput {
  applicantName: string;
  run: string;
  examDate: string;
  examinerName: string;
  municipality: string;
  startedAt: string;
  endedAt: string;
  kmStart: number | null;
  kmEnd: number | null;
  school: string;
  schoolCommune: string;
  plate: string;
  vehicleType: PracticalExamVehicle | null;
  faults: Record<string, number>;
  observations: string;
  studentSignature: string;
  examinerSignature: string;
  status: PracticalExamSheetStatus;
}

function faults(
  blockId: string,
  rows: Array<[code: string, label: string, severity: PracticalExamSeverity]>,
): PracticalExamFault[] {
  return rows.map(([code, label, severity]) => ({
    id: `${blockId}:${code}`,
    code,
    label,
    severity,
  }));
}

export const CLASS_B_PRACTICAL_EXAM: PracticalExamBlock[] = [
  {
    id: 'previas',
    title: 'Comprobaciones previas',
    column: 'left',
    faults: faults('previas', [
      ['L1', 'Documentos', 'L'],
      ['L2', 'Ajuste de espejo y asiento', 'L'],
      ['R1', 'Sin cinturón, conductor y pasajero', 'R'],
      ['L3', 'Arranque con el motor en marcha', 'L'],
      ['L4', 'No quitar el freno de estacionamiento', 'L'],
      ['L5', 'Puertas mal cerradas', 'L'],
      ['L21', 'No identificar elementos de seguridad', 'L'],
    ]),
  },
  {
    id: 'ingreso',
    title: 'Ingreso y salida a la circulación',
    column: 'left',
    faults: faults('ingreso', [
      ['L7', 'No señalizar o hacerlo mal', 'L'],
      ['G1', 'Obstaculizar', 'G'],
      ['R2', 'Generar riesgo', 'R'],
      ['L8', 'No quitar la señal', 'L'],
    ]),
  },
  {
    id: 'circulacion',
    title: 'Circulación normal',
    column: 'left',
    faults: faults('circulacion', [
      ['R5', 'Exceso de velocidad', 'R'],
      ['G13', 'No respetar distancias', 'G'],
      ['G18', 'Velocidad no prudente', 'G'],
      ['G20', 'Mojar a peatones o ciclistas', 'G'],
      ['R7', 'Manipular elementos distractores', 'R'],
      ['L23', 'Retroceder donde está prohibido', 'L'],
    ]),
  },
  {
    id: 'pista',
    title: 'Cambio de pista',
    column: 'left',
    faults: faults('pista', [
      ['G1', 'Obstaculizar', 'G'],
      ['G2', 'No señalizar o hacerlo mal', 'G'],
      ['L6', 'No quitar la señal', 'L'],
      ['R2', 'Generar riesgo', 'R'],
    ]),
  },
  {
    id: 'virajes',
    title: 'Virajes',
    column: 'left',
    faults: faults('virajes', [
      ['L7', 'No señalizar o hacerlo mal', 'L'],
      ['L8', 'No quitar la señal', 'L'],
      ['L9', 'Subir la cuneta o solera', 'L'],
      ['L21', 'Virar desde pista no permitida', 'L'],
      ['G1', 'Obstaculizar', 'G'],
      ['R2', 'Generar riesgo', 'R'],
    ]),
  },
  {
    id: 'intersecciones',
    title: 'Intersecciones',
    column: 'left',
    faults: faults('intersecciones', [['G5', 'Ingresar bloqueando el cruce', 'G']]),
  },
  {
    id: 'demarcaciones',
    title: 'Demarcaciones',
    column: 'left',
    faults: faults('demarcaciones', [
      ['L13', 'No respetarlas', 'L'],
      ['L25', 'Posición incorrecta', 'L'],
    ]),
  },
  {
    id: 'adelantamientos',
    title: 'Adelantamientos y sobrepasos',
    column: 'right',
    faults: faults('adelantamientos', [
      ['G3', 'Paso peatonal o cruce no regulados', 'G'],
      ['G4', 'Riesgo en sentido contrario', 'G'],
      ['G17', 'En zonas no permitidas', 'G'],
    ]),
  },
  {
    id: 'estacionamiento',
    title: 'Estacionamiento',
    column: 'right',
    faults: faults('estacionamiento', [
      ['L10', 'Separación de 30 cm y 60 cm', 'L'],
      ['L11', 'No poner el freno de estacionamiento', 'L'],
      ['L12', 'Subir, forzar o golpear cuneta o solera', 'L'],
      ['G6', 'Bajar sin observar el tránsito', 'G'],
      ['G15', 'En un lugar no permitido', 'G'],
      ['G19', 'Demorar más de tres minutos', 'G'],
      ['L22', 'No detener el motor', 'L'],
      ['L26', 'No girar las ruedas en vías inclinadas', 'L'],
    ]),
  },
  {
    id: 'manejo',
    title: 'Manejo del vehículo',
    column: 'right',
    faults: faults('manejo', [
      ['R4', 'Golpear con el vehículo a alguien o algo', 'R'],
      ['L14', 'Marchas inadecuadas o neutro', 'L'],
      ['G10', 'Confusión de pedales', 'G'],
      ['L24', 'Embrague accionado', 'L'],
      ['G11', 'Soltar una o dos manos del volante', 'G'],
      ['G12', 'Circular en sentido contrario', 'G'],
      ['L17', 'Conducir de forma brusca', 'L'],
      ['L20', 'Detención del motor sin obstaculizar', 'L'],
      ['G16', 'Detención del motor obstaculizando', 'G'],
      ['L19', 'No controlar en vías inclinadas', 'L'],
    ]),
  },
  {
    id: 'observacion',
    title: 'Observación',
    column: 'right',
    faults: faults('observacion', [['L18', 'No observar el tránsito', 'L']]),
  },
  {
    id: 'senales',
    title: 'Señales',
    column: 'right',
    faults: faults('senales', [
      ['R3', 'Señal PARE o luz roja', 'R'],
      ['G7', 'Señal CEDA EL PASO', 'G'],
      ['G8', 'Señales de prohibición o restricción', 'G'],
      ['R6', 'No obedecer a Carabineros', 'R'],
    ]),
  },
  {
    id: 'luces',
    title: 'Luces',
    column: 'right',
    faults: faults('luces', [['G9', 'No encender las luces', 'G']]),
  },
  {
    id: 'preferencias',
    title: 'Preferencias',
    column: 'right',
    faults: faults('preferencias', [['G14', 'Peatones, ciclistas y otros vehículos', 'G']]),
  },
  {
    id: 'mandos',
    title: 'Mandos del vehículo',
    column: 'right',
    faults: faults('mandos', [
      ['L15', 'Usar la bocina sin motivo o donde está prohibido', 'L'],
      ['L16', 'No identificar los mandos', 'L'],
    ]),
  },
];

export const PRACTICAL_EXAM_FAIL_RULE =
  '1 reprobatoria · 2 graves · 1 grave y 5 leves · 10 leves';

const knownFaultIds = new Set(
  CLASS_B_PRACTICAL_EXAM.flatMap((block) => block.faults.map((fault) => fault.id)),
);

export function isPracticalExamFaultId(id: string): boolean {
  return knownFaultIds.has(id);
}

export function practicalExamFails(totals: PracticalExamTotals): boolean {
  return (
    totals.failing >= 1 ||
    totals.serious >= 2 ||
    (totals.serious >= 1 && totals.mild >= 5) ||
    totals.mild >= 10
  );
}

function emptyTotals(): PracticalExamTotals {
  return { mild: 0, serious: 0, failing: 0 };
}

export function scorePracticalExam(counts: Record<string, number>): PracticalExamScore {
  const subtotals: Record<string, PracticalExamTotals> = {};
  const totals = emptyTotals();

  for (const block of CLASS_B_PRACTICAL_EXAM) {
    const sub = emptyTotals();
    for (const fault of block.faults) {
      const count = counts[fault.id] ?? 0;
      if (count <= 0) continue;
      if (fault.severity === 'L') sub.mild += count;
      else if (fault.severity === 'G') sub.serious += count;
      else sub.failing += count;
    }
    subtotals[block.id] = sub;
    totals.mild += sub.mild;
    totals.serious += sub.serious;
    totals.failing += sub.failing;
  }

  return { ...totals, passed: !practicalExamFails(totals), subtotals };
}
